/**
 * ChalkButton — vibrant game-style button.
 *
 * Primary:   bold red gradient (waxRed) — the loud CTA
 * Secondary: ocean blue gradient (#1d4ed8) — navigation
 * Danger:    same as primary (destructive)
 *
 * Optimised for mobile: minimum 48px tap target, larger text, clear
 * contrast. Spring hover/tap animations via motion/react.
 */

import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import { motion, type MotionStyle } from 'motion/react';
import { fontStacks } from '@qd/design-tokens';

type ChalkButtonVariant = 'primary' | 'secondary' | 'danger';
type ChalkButtonSize = 'sm' | 'md' | 'lg';

interface ChalkButtonProps {
  children: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: ChalkButtonVariant;
  size?: ChalkButtonSize;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
  rotation?: number;
  fullWidth?: boolean;
}

interface VariantStyle {
  bg: string;
  fg: string;
  shadow: string;
}

function variantStyles(variant: ChalkButtonVariant, disabled: boolean): VariantStyle {
  if (disabled) {
    return {
      bg: 'rgba(100,100,120,0.35)',
      fg: 'rgba(255,255,255,0.4)',
      shadow: 'none',
    };
  }
  switch (variant) {
    case 'secondary':
      return {
        bg: 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)',
        fg: '#ffffff',
        shadow: '0 6px 0 #1e3a8a, 0 10px 24px rgba(29,78,216,0.45)',
      };
    case 'danger':
      return {
        bg: 'linear-gradient(180deg, #f87171 0%, #ef4444 50%, #dc2626 100%)',
        fg: '#ffffff',
        shadow: '0 6px 0 #991b1b, 0 10px 24px rgba(239,68,68,0.45)',
      };
    case 'primary':
    default:
      return {
        bg: 'linear-gradient(180deg, #f87171 0%, #ef4444 50%, #dc2626 100%)',
        fg: '#ffffff',
        shadow: '0 6px 0 #991b1b, 0 10px 24px rgba(239,68,68,0.45)',
      };
  }
}

function sizeStyles(size: ChalkButtonSize) {
  switch (size) {
    case 'sm':
      // min 44px height for mobile tap targets
      return { padding: '10px 20px', fontSize: 15, letterSpacing: '0.12em', minHeight: 44 };
    case 'lg':
      return { padding: '20px 44px', fontSize: 24, letterSpacing: '0.16em', minHeight: 64 };
    case 'md':
    default:
      return { padding: '14px 32px', fontSize: 18, letterSpacing: '0.14em', minHeight: 52 };
  }
}

export const ChalkButton = forwardRef<HTMLButtonElement, ChalkButtonProps>(function ChalkButton(
  {
    children,
    onClick,
    variant = 'primary',
    size = 'md',
    disabled = false,
    type = 'button',
    ariaLabel,
    className,
    style,
    fullWidth,
  },
  ref,
) {
  const v = variantStyles(variant, disabled);
  const s = sizeStyles(size);

  const baseShadow = disabled ? 'none' : v.shadow;
  const hoverShadow = disabled ? 'none' : v.shadow.replace('0 6px', '0 8px').replace('10px', '16px');
  const tapShadow  = disabled ? 'none' : '0 2px 0 rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.25)';

  const hoverActiveProps = disabled
    ? {}
    : {
        whileHover: { scale: 1.04, boxShadow: hoverShadow, y: -3 },
        whileTap:   { scale: 0.97, boxShadow: tapShadow,   y: 4  },
      };

  return (
    <motion.button
      ref={ref}
      type={type}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      className={className}
      initial={{ scale: 1, boxShadow: baseShadow, y: 0 }}
      animate={{ scale: 1, boxShadow: baseShadow, y: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      {...hoverActiveProps}
      style={
        ({
          position: 'relative',
          background: v.bg,
          color: v.fg,
          border: 'none',
          borderRadius: 14,
          fontFamily: fontStacks.display,
          fontWeight: 800,
          textTransform: 'uppercase',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          outlineOffset: 4,
          width: fullWidth ? '100%' : undefined,
          padding: s.padding,
          fontSize: s.fontSize,
          letterSpacing: s.letterSpacing,
          minHeight: s.minHeight,
          // Ensure text-shadow for readability
          textShadow: disabled ? 'none' : '0 1px 3px rgba(0,0,0,0.35)',
          // Touch action for better mobile performance
          touchAction: 'manipulation',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          ...style,
        } as CSSProperties) as MotionStyle
      }
      onFocus={(e) => {
        e.currentTarget.style.outline = '3px solid rgba(255,255,255,0.8)';
        e.currentTarget.style.outlineOffset = '4px';
      }}
      onBlur={(e) => {
        e.currentTarget.style.outline = 'none';
      }}
    >
      {children}
    </motion.button>
  );
});
